import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { listEvents } from '../../controllers/events';
import EventCreateModal from '../events/EventCreateModal';

const CAPACITACION_EVENT_TYPE = 'Capacitacion Integral Progresiva';

function formatSessionTime(value) {
  if (!value) {
    return '-';
  }
  return String(value).slice(0, 5);
}

function ComunidadCapacitacionTab({
  token,
  ciclo,
  comunidadId,
  regionId,
  diocesisId,
  canManageEvents,
}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [error, setError] = useState('');
  const [okMessage, setOkMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const listFilters = useMemo(() => ({
    cycleYear: ciclo,
    eventType: CAPACITACION_EVENT_TYPE,
    comunidadId,
    statusFilter,
  }), [ciclo, comunidadId, statusFilter]);

  useEffect(() => {
    loadEvents();
  }, [token, listFilters.cycleYear, listFilters.comunidadId, listFilters.statusFilter]);

  async function loadEvents() {
    if (!token || !comunidadId) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const rows = await listEvents(token, listFilters);
      setEvents(Array.isArray(rows) ? rows : []);
    }
    catch (e) {
      setError(e.message || 'No se pudieron cargar los eventos de capacitacion.');
    }
    finally {
      setLoading(false);
    }
  }

  async function handleCreatedEvent() {
    setOkMessage('Evento de capacitacion creado correctamente.');
    await loadEvents();
  }

  function openDetails(eventItem) {
    setSelectedEvent(eventItem);
    setShowDetailModal(true);
  }

  return (
    <div className="pt-3">
      {!!error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {!!okMessage && <Alert variant="success" onClose={() => setOkMessage('')} dismissible>{okMessage}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-end g-3">
            <Col md={3}>
              <Form.Label>Estatus</Form.Label>
              <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">Todos</option>
                <option value="upcoming">Programados</option>
                <option value="past">Pasados</option>
              </Form.Select>
            </Col>
            <Col className="d-flex justify-content-end gap-2">
              <Button variant="outline-secondary" onClick={loadEvents}>Actualizar</Button>
              {canManageEvents && (
                <Button onClick={() => setShowModal(true)}>Crear capacitacion</Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Eventos de capacitacion</strong>
          <Badge bg="secondary">{events.length}</Badge>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="py-4 text-center"><Spinner animation="border" /></div>
          ) : events.length === 0 ? (
            <Alert variant="info" className="mb-0">No hay eventos de capacitacion para este ciclo.</Alert>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Titulo</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Registro</th>
                  <th>Pago</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.event_id}>
                    <td>{event.title}</td>
                    <td>{event.starts_on}</td>
                    <td>{event.ends_on}</td>
                    <td>{event.registration_status}</td>
                    <td>{event.payment_type}{event.fee_amount ? ` ($${event.fee_amount})` : ''}</td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => openDetails(event)}>Ver detalles</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <EventCreateModal
        show={showModal}
        onHide={() => setShowModal(false)}
        token={token}
        defaultCycleYear={ciclo}
        fixedEventType={CAPACITACION_EVENT_TYPE}
        fixedVisibilityLevel={4}
        fixedRegionId={regionId}
        fixedDiocesisId={diocesisId}
        fixedComunidadId={comunidadId}
        modalTitle="Crear evento de capacitacion"
        onCreated={handleCreatedEvent}
      />

      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedEvent?.title || 'Detalle de evento'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!selectedEvent ? (
            <Alert variant="warning" className="mb-0">No se pudo cargar el detalle del evento.</Alert>
          ) : (
            <>
              <Row className="mb-3 small">
                <Col md={4}><strong>Inicio:</strong> {selectedEvent.starts_on}</Col>
                <Col md={4}><strong>Fin:</strong> {selectedEvent.ends_on}</Col>
                <Col md={4}><strong>Registro:</strong> {selectedEvent.registration_status}</Col>
                <Col md={4}><strong>Pago:</strong> {selectedEvent.payment_type}</Col>
                <Col md={8}><strong>Cuota:</strong> {selectedEvent.fee_amount ? `$${selectedEvent.fee_amount}` : 'No aplica'}</Col>
              </Row>

              <h6>Sesiones</h6>
              {(selectedEvent.event_sessions || []).length === 0 ? (
                <Alert variant="info" className="mb-0">No hay sesiones registradas para este evento.</Alert>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {(selectedEvent.event_sessions || []).map((session) => (
                    <Card key={session.event_session_id || `${session.session_date}-${session.start_time}`}>
                      <Card.Body className="small">
                        <Row className="g-2">
                          <Col md={4}><strong>Fecha:</strong> {session.session_date || '-'}</Col>
                          <Col md={4}><strong>Hora inicio:</strong> {formatSessionTime(session.start_time)}</Col>
                          <Col md={4}><strong>Hora fin:</strong> {formatSessionTime(session.end_time)}</Col>
                          <Col md={6}><strong>Lugar:</strong> {session.location_name || 'Sin lugar definido'}</Col>
                          <Col md={6}><strong>Direccion:</strong> {session.location_address || 'Sin direccion definida'}</Col>
                          <Col md={12}>
                            <strong>Enlace:</strong>{' '}
                            {session.meeting_url ? (
                              <a href={session.meeting_url} target="_blank" rel="noreferrer">{session.meeting_url}</a>
                            ) : (
                              'Sin enlace'
                            )}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {canManageEvents && selectedEvent?.event_id && (
            <Link to={`/app/admin/eventos/${selectedEvent.event_id}`} className="btn btn-outline-secondary">Administrar evento</Link>
          )}
          <Button variant="primary" onClick={() => setShowDetailModal(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ComunidadCapacitacionTab;
